import { Link, useParams } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import { Crumbs, Rich } from "@/components/content/ContentBits";
import { useContent } from "@/content/store";
import { Block } from "@/pages/ArticlePage";
import NotFound from "@/pages/NotFound";
import { TagGenerator } from "@/components/tools/TagGenerator";
import { TitleGenerator } from "@/components/tools/TitleGenerator";
import { SeoChecker } from "@/components/tools/SeoChecker";

export const TOOLS = [
  { slug: "etsy-tag-generator", name: "Etsy Tag Generator", blurb: "Describe your product, get 13 Etsy-valid tag suggestions to copy.", W: TagGenerator },
  { slug: "etsy-title-generator", name: "Etsy Title Generator", blurb: "Three clear titles of 140 characters or fewer, plus shop title ideas.", W: TitleGenerator },
  { slug: "etsy-seo-checker", name: "Etsy SEO Checker", blurb: "Paste a title, tags and description for a 0–100 score with fixes.", W: SeoChecker },
];

const Explainer = ({ a }) => (
  <div data-testid="tool-explainer" className="mt-16 text-[17px] leading-[1.75] text-muted-foreground">
    {a.sections.map((s, si) => (
      <section key={s.h2}>
        <h2 className="mt-12 font-display text-2xl leading-snug text-foreground sm:text-3xl">{s.h2}</h2>
        {s.blocks.map((b, bi) => <Block key={bi} b={b} i={`${si}-${bi}`} />)}
      </section>
    ))}
    <section data-testid="tool-faq">
      <h2 className="mt-14 font-display text-2xl text-foreground sm:text-3xl">FAQ</h2>
      {a.faq.map((f) => <div key={f.q} className="mt-6"><h3 className="font-semibold text-foreground">{f.q}</h3><p className="mt-2"><Rich text={f.a} /></p></div>)}
    </section>
    <p className="mt-12 border-t border-white/10 pt-6 text-xs">Free, no sign-up. Suggestions are generated from the details you enter — validate them with your own research. Check Etsy's current rules in the <a href="https://help.etsy.com/hc/en-us" rel="nofollow noopener" target="_blank" className="underline underline-offset-4">Etsy Help Center</a>.</p>
  </div>
);

export default function ToolPage() {
  const { slug } = useParams();
  const tool = TOOLS.find((t) => t.slug === slug);
  const a = useContent(`/content/tools/${slug}`, true);
  if (!tool || a?.notFound) return <NotFound />;
  const W = tool.W;
  return (
    <PublicLayout>
      <article data-testid={`tool-page-${slug}`} className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14">
        <Crumbs testid="tool-breadcrumbs" items={[["Home", "/"], ["Free tools", "/tools"], [tool.name]]} />
        <h1 data-testid="tool-h1" className="font-display text-4xl leading-[1.08] sm:text-5xl">{a?.h1 || tool.name}</h1>
        {a?.intro && <p data-testid="tool-intro" className="mt-5 text-lg leading-relaxed text-foreground/85"><Rich text={a.intro} /></p>}
        <div className="glass hairline-gold mt-10 rounded-[28px] border p-5 sm:p-8"><W /></div>
        {a?.form_note && <p data-testid="tool-form-note" className="mt-6 text-[15px] leading-relaxed text-muted-foreground"><Rich text={a.form_note} /></p>}
        {a?.image_alt && (
          <figure data-testid="tool-figure" className="mt-12 overflow-hidden rounded-[24px] border border-white/10">
            <img src={`/images/tools/${slug}.webp`} alt={a.image_alt} width="1200" height="800" loading="lazy" decoding="async"
              data-testid="tool-image" className="block h-auto w-full" />
          </figure>
        )}
        {a?.sections && <Explainer a={a} />}
        <nav aria-label="More free tools" data-testid="tool-more" className="mt-14 flex flex-wrap gap-3 text-sm">
          {TOOLS.filter((t) => t.slug !== slug).map((t) => <Link key={t.slug} to={`/tools/${t.slug}`} data-testid={`tool-more-${t.slug}`} className="inline-flex min-h-[44px] items-center rounded-full border border-white/10 px-5 hover:border-[#E9C98B]/40">{t.name} →</Link>)}
          <Link to="/tools" className="inline-flex min-h-[44px] items-center px-3 text-[#5EE6D0] underline underline-offset-4">All free tools</Link>
        </nav>
      </article>
    </PublicLayout>
  );
}
